import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { encodeDate } from '@campfire/hot-date';
import { LinearProgressOverlay } from '@campfire/linear-progress-overlay';
import { TabletButton } from '@campfire/tablet-button';
import { Box } from '@material-ui/core';
import { Form, Formik } from 'formik';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { string as YupString, array as YupArray } from 'yup';
import { FormikErrorFocus } from '../../common/form/FormikErrorFocus';
import { volunteerProfileHardCodedFields } from '../../common/form/task-form/model/get-volunteer-profile-hardcoded-field-values';
import { initTaskFormFormikValues, TaskFormValuesType } from '../../common/form/task-form/model/task-form-init';
import {
  dateValidator,
  dynamicValidationSchema,
  phoneNumberValidator,
} from '../../common/form/task-form/model/validation-schema';
import { TaskFormItem } from '../../common/form/task-form/model/__generated__/TaskFormItem';
import { TaskFormFieldList } from '../../common/form/task-form/TaskFormFieldList';
import {
  DateTaskFormikField,
  PhoneTaskFormikField,
  ShortTextTaskFormikField,
} from '../../common/inputs/task-fields/TaskFormikFields';
import { TaskFieldContainer } from '../../common/inputs/containers/TaskFieldContainer';
import { deserializeTaskItems } from '../../screens/admin/admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/utils';
import { useUser } from '../auth/useUser';
import { useSnackbar } from '../config/useSnackbar';
import { useEndpointFetch } from '../network/useEndpointFetch';
import { getTaskFieldValues, TaskFieldValueType } from './model/suyp-task-submission-handler';
import { SUYPTaskScaffold } from './SUYPTaskScaffold';
import { SUYPProgramPicker } from './SUYPProgramPicker';
import { sortProgramsByDisplayOrder } from '../../common/functions/sort-programs';

const buildValidationSchema = (programLabel: string, allowMulti: boolean, dobRequired: boolean) => ({
  firstName: YupString().required('Required'),
  lastName: YupString().required('Required'),
  preferredName: YupString().nullable(),
  dob: dateValidator(!dobRequired),
  contactNumber: phoneNumberValidator(false),
  programIds: YupArray()
    .of(YupString())
    .min(
      1,
      allowMulti
        ? `Please select at least one ${programLabel.toLowerCase()}`
        : `Please select a ${programLabel.toLowerCase()}`
    ),
  taskId: YupString().required('Required'),
});

type VolProfileInitialValues = {
  firstName: string;
  lastName: string;
  preferredName: string;
  dob: null | DateTime;
  contactNumber: string;
  programIds: string[];
  [key: string]: any;
};

type VolunteerProfileSubmission = {
  firstName: string;
  lastName: string;
  preferredName: string;
  dob: string | null;
  contactNumber: string;
  programIds: string[];
  taskId: string;
  taskFieldValues: TaskFieldValueType[];
  userId: string;
};

interface FunnelProgramData {
  lockPrograms: boolean;
  programIds: string[];
}

interface SUYPVolunteerProfileProps {
  task?: {
    taskId: string;
  };
  taskItems: TaskFormItem[];
  programs: {
    programId: string;
    name: string;
    description: string | null;
    dateSuspended: any | null;
    displayOrder?: number | null;
  }[];
  refetch?: () => void;
  loading: boolean;
  allowMultiProgramApplication?: boolean;
  funnelData?: FunnelProgramData;
  programLabel?: string;
  existingProfile?: {
    contactNumber: string | null;
    dateOfBirth: any | null;
    programs: { programId: string }[];
  } | null;
}

export const SUYPVolunteerProfile = (props: SUYPVolunteerProfileProps) => {
  const {
    taskItems,
    task,
    refetch,
    loading,
    programs,
    allowMultiProgramApplication = false,
    funnelData,
    programLabel = 'Program',
    existingProfile,
  } = props;
  const { setSnackbar } = useSnackbar();
  const { user } = useUser();
  const dobRequired = user.configItems?.vm?.requireDateOfBirth !== false;

  const submitVolunteerProfile = useEndpointFetch<VolunteerProfileSubmission>(
    '/vm/volunteer/profile/completed-task/save-with-profile'
  );

  const initialFormikValues = useMemo(() => {
    return {
      ...initTaskFormFormikValues(taskItems, undefined),
      taskId: task?.taskId,
      firstName: user.firstName,
      lastName: user.lastName,
      preferredName: '',
      dob: existingProfile?.dateOfBirth ? DateTime.fromISO(existingProfile.dateOfBirth) : null,
      contactNumber: existingProfile?.contactNumber ?? '',
      programIds: funnelData?.programIds?.length
        ? funnelData.programIds
        : existingProfile?.programs.map((program) => program.programId) ?? ([] as string[]),
    };
  }, [taskItems, funnelData, existingProfile]);

  const parsedTaskItems = deserializeTaskItems(taskItems);

  const baseValidation = buildValidationSchema(programLabel, allowMultiProgramApplication, dobRequired);
  const validationSchema = dynamicValidationSchema(parsedTaskItems, baseValidation, funnelData?.programIds);

  const handleSubmit = async (values: VolProfileInitialValues) => {
    const taskFieldValues = await getTaskFieldValues(parsedTaskItems, values);

    submitVolunteerProfile
      .run({
        ...values,
        dob: values.dob ? encodeDate(values.dob) : null,
        preferredName: values.preferredName !== '' ? values.preferredName : values.firstName,
        taskFieldValues,
        taskId: task?.taskId ?? '',
        userId: user.userId,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to save volunteer profile',
            variant: 'error',
          });
          return;
        }

        setSnackbar({
          open: true,
          message: 'Volunteer profile saved',
          variant: 'success',
        });
        if (refetch) refetch();
      })
      .catch(() => {});
  };

  const orderedPrograms = useMemo(() => {
    let filtered = programs.filter((program) => program.dateSuspended === null);
    if (funnelData && funnelData.programIds.length > 0) {
      filtered = filtered.filter((p) => funnelData.programIds.includes(p.programId));
    }
    return sortProgramsByDisplayOrder(filtered);
  }, [programs, funnelData]);

  return loading ? (
    <CircularProgressOverlay isLoading />
  ) : (
    <Box bgcolor='#f1f1f1' display='flex' flex='1 1 auto' flexDirection='column' width={1} paddingTop={2}>
      <LinearProgressOverlay isLoading={submitVolunteerProfile.isLoading} />

      <SUYPTaskScaffold
        allowBack={false}
        taskHeaderDetails={{ title: `Hey, ${user.preferredName}`, description: `Let's get your profile started` }}
      >
        <Formik initialValues={initialFormikValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
          {({ values: fullValues, setFieldValue, errors, touched }) => {
            const {
              taskId: _unusedTaskId,
              firstName: _unusedFirstTime,
              lastName: _unusedLastTime,
              preferredName: _unusedPreferredName,
              dob: _unusedDOB,
              contactNumber: _unusedContactNumber,
              programIds,
              ...values
            } = fullValues;

            return (
              <Form>
                <Box display='flex' justifyContent='center' flex='1 1 auto'>
                  <Box maxWidth={720} width={1}>
                    <ShortTextTaskFormikField
                      name='firstName'
                      ariaId='firstName'
                      title={volunteerProfileHardCodedFields.firstName.name}
                      ariaDescribedBy='First name'
                      required
                      outlinedInputProps={{ autoFocus: true }}
                      autoComplete='no'
                    />
                    <ShortTextTaskFormikField
                      name='lastName'
                      ariaId='lastName'
                      title={volunteerProfileHardCodedFields.lastName.name}
                      ariaDescribedBy='Last Name'
                      required
                      autoComplete='no'
                    />
                    <ShortTextTaskFormikField
                      name='preferredName'
                      ariaId='preferredName'
                      title={volunteerProfileHardCodedFields.preferredName.name}
                      description={volunteerProfileHardCodedFields.preferredName.description}
                      ariaDescribedBy='Preferred Name'
                      autoComplete='no'
                    />
                    <DateTaskFormikField
                      name='dob'
                      ariaId='dob'
                      title={volunteerProfileHardCodedFields.dob.name}
                      ariaDescribedBy='Date of Birth'
                      required={dobRequired}
                    />
                    <PhoneTaskFormikField
                      name='contactNumber'
                      ariaId='contactNumber'
                      title={volunteerProfileHardCodedFields.contactNumber.name}
                      description={volunteerProfileHardCodedFields.contactNumber.description}
                      ariaDescribedBy='Phone number'
                      required
                    />
                    <TaskFieldContainer
                      title={`Choose a ${programLabel}`}
                      description={`Select a ${programLabel.toLowerCase()} to start with`}
                      ariaId='programIds'
                      ariaDescribedBy={programLabel}
                      required
                      error={touched.programIds && Boolean(errors.programIds)}
                      helperText={touched.programIds && errors.programIds ? String(errors.programIds) : undefined}
                    >
                      <SUYPProgramPicker
                        programs={orderedPrograms}
                        multiple={!!allowMultiProgramApplication}
                        disabled={funnelData?.lockPrograms}
                        value={programIds}
                        onChange={(ids) => setFieldValue('programIds', ids)}
                        programLabel={programLabel}
                      />
                    </TaskFieldContainer>

                    <TaskFormFieldList
                      taskItems={deserializeTaskItems(taskItems)}
                      disableAutoFocus
                      values={values as TaskFormValuesType}
                      volunteerProgramIds={programIds}
                    />

                    <TabletButton
                      aria-label='submit'
                      color='primary'
                      variant='contained'
                      type='submit'
                      size='large'
                      style={{ width: 256 }}
                    >
                      Submit
                    </TabletButton>
                  </Box>
                </Box>
                <FormikErrorFocus />
              </Form>
            );
          }}
        </Formik>
      </SUYPTaskScaffold>
    </Box>
  );
};
